import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap, Mail, Lock, ArrowRight, AlertCircle, Eye, EyeOff,
  Activity, Cpu, Database, Globe, Server, Shield,
} from "lucide-react";
import { loginEngine } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

/* ── Live Clock ── */
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <span style={{ fontFamily: "var(--el-mono)", fontSize: 11, color: "rgba(147,197,253,0.45)", letterSpacing: "0.1em" }}>
      {time.toLocaleTimeString("en-US", { hour12: false })}
    </span>
  );
}

/* ── Animated grid background ── */
function CyberGrid() {
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: `
        linear-gradient(rgba(59,130,246,0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(59,130,246,0.045) 1px, transparent 1px)
      `,
      backgroundSize: "48px 48px",
      maskImage: "radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)",
    }} />
  );
}

/* ── Scan line ── */
function ScanLine() {
  return (
    <motion.div
      style={{
        position: "absolute", left: 0, right: 0, height: 2,
        background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.3), rgba(147,197,253,0.55), rgba(59,130,246,0.3), transparent)",
        filter: "blur(1px)", pointerEvents: "none", zIndex: 2,
      }}
      animate={{ top: ["0%", "100%"] }}
      transition={{ duration: 5, repeat: Infinity, ease: "linear", repeatDelay: 3 }}
    />
  );
}

/* ── Floating data nodes ── */
function DataNodes() {
  const nodes = useRef(
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      x: 5 + Math.random() * 90,
      y: 5 + Math.random() * 90,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 8,
      delay: Math.random() * 6,
    }))
  ).current;
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {nodes.map((n) => (
        <motion.div key={n.id}
          style={{
            position: "absolute", left: `${n.x}%`, top: `${n.y}%`,
            width: n.size, height: n.size, borderRadius: "50%",
            background: "rgba(147,197,253,0.55)",
            boxShadow: "0 0 6px rgba(147,197,253,0.55)",
          }}
          animate={{ opacity: [0, 0.8, 0], scale: [0.5, 1.2, 0.5] }}
          transition={{ duration: n.duration, delay: n.delay, repeat: Infinity }}
        />
      ))}
    </div>
  );
}

/* ── Loading stages progress ── */
function LoadingStages({ stage }) {
  const stages = ["Connecting", "Authenticating", "Authorizing", "Granting Access"];
  return (
    <div style={{ width: "100%", marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        {stages.map((s, i) => (
          <motion.div key={s}
            style={{
              width: 8, height: 8, borderRadius: "50%",
              background: i < stage ? "#3B82F6" : "rgba(59,130,246,0.15)",
              boxShadow: i < stage ? "0 0 10px rgba(59,130,246,0.85)" : "none",
            }}
            animate={i < stage ? { scale: [1, 1.45, 1] } : {}}
            transition={{ duration: 0.5, repeat: i === stage - 1 ? Infinity : 0 }}
          />
        ))}
      </div>
      <div style={{ position: "relative", height: 3, background: "rgba(59,130,246,0.1)", borderRadius: 3, overflow: "hidden" }}>
        <motion.div
          style={{ height: "100%", background: "linear-gradient(90deg, #1E3A8A, #3B82F6, #93C5FD)", borderRadius: 3 }}
          animate={{ width: `${(stage / stages.length) * 100}%` }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        />
        <motion.div
          style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: "40%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)" }}
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 1.2, repeat: stage > 0 && stage < 4 ? Infinity : 0, ease: "easeInOut" }}
        />
      </div>
      <motion.p key={stage}
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        style={{ fontFamily: "var(--el-mono)", fontSize: 10, color: "rgba(147,197,253,0.45)", marginTop: 7, letterSpacing: "0.06em" }}>
        {stage > 0 ? `> ${stages[Math.min(stage - 1, 3)]}...` : "> Idle"}
      </motion.p>
    </div>
  );
}

const FEATURES = [
  { icon: Zap,      label: "High-performance payment engine",    color: "#60A5FA" },
  { icon: Cpu,      label: "Real-time processing & monitoring",  color: "#93C5FD" },
  { icon: Database, label: "Transaction routing & management",   color: "#FBBF24" },
  { icon: Activity, label: "Advanced analytics & reporting",     color: "#34D399" },
  { icon: Globe,    label: "Multi-region engine deployment",     color: "#A78BFA" },
  { icon: Shield,   label: "Engine security & compliance",       color: "#38BDF8" },
];

/* ══════════════════ MAIN COMPONENT ══════════════════ */
export default function EngineLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [focused,  setFocused]  = useState("");
  const [stage,    setStage]    = useState(0);
  const [success,  setSuccess]  = useState(false);

  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const h = (e) => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setMouse({
        x: ((e.clientX - r.left) / r.width) * 100,
        y: ((e.clientY - r.top) / r.height) * 100,
      });
    };
    window.addEventListener("mousemove", h, { passive: true });
    return () => window.removeEventListener("mousemove", h);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError(t("pleaseEnterBothEmailAndPassword") || "Please enter both email and password");
      return;
    }
    setLoading(true);
    const wait = (ms) => new Promise(res => setTimeout(res, ms));
    try {
      setStage(1); await wait(600);
      setStage(2);
      await loginEngine(email, password);
      await wait(500); setStage(3);
      await wait(500); setStage(4);
      setSuccess(true);
      setTimeout(() => navigate("/EngineDashboard"), 950);
    } catch (err) {
      setError(err.message || t("loginFailed") || "Authentication failed");
      setLoading(false);
      setStage(0);
    }
  };

  return (
    <div ref={containerRef} style={{ minHeight: "100vh", background: "var(--el-bg)", display: "flex", flexDirection: "column", fontFamily: "var(--el-sans)", position: "relative", overflow: "hidden" }}>

      {/* ══ GLOBAL STYLES ══ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&family=Syne:wght@700;800&display=swap');

        :root {
          --el-bg:      #050c1a;
          --el-bg2:     #070e1f;
          --el-blue:    #3B82F6;
          --el-blue2:   #60A5FA;
          --el-blue3:   #93C5FD;
          --el-indigo:  #6366F1;
          --el-t1:      #EFF6FF;
          --el-t2:      #BFDBFE;
          --el-t3:      #60A5FA;
          --el-t4:      #1E40AF;
          --el-sans:    'Plus Jakarta Sans', sans-serif;
          --el-display: 'Syne', sans-serif;
          --el-mono:    'JetBrains Mono', monospace;
        }
        *, *::before, *::after { box-sizing: border-box; }

        /* ── Input ── */
        .el-input {
          width: 100%; height: 52px;
          background: rgba(14,26,58,0.85);
          border: 1.5px solid rgba(59,130,246,0.16);
          border-radius: 13px;
          color: #EFF6FF;
          font-size: 14px; font-family: var(--el-sans); font-weight: 500;
          padding: 0 48px;
          outline: none; caret-color: #3B82F6;
          transition: border-color 0.22s, background 0.22s, box-shadow 0.22s;
          letter-spacing: 0.01em;
        }
        .el-input::placeholder { color: rgba(147,197,253,0.22); }
        .el-input:hover  { border-color: rgba(59,130,246,0.32); background: rgba(16,30,68,0.9); }
        .el-input:focus  {
          border-color: rgba(59,130,246,0.7);
          background: rgba(18,34,76,0.95);
          box-shadow: 0 0 0 4px rgba(59,130,246,0.1), 0 4px 20px rgba(59,130,246,0.08);
        }

        /* Autofill */
        .el-input:-webkit-autofill,
        .el-input:-webkit-autofill:hover,
        .el-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px rgba(10,20,50,0.98) inset !important;
          -webkit-text-fill-color: #EFF6FF !important;
          transition: background-color 9999s;
        }

        /* ── Submit button ── */
        .el-btn {
          width: 100%; height: 52px;
          background: linear-gradient(135deg, #1E3A8A 0%, #2563EB 50%, #3B82F6 100%);
          border: none; border-radius: 13px;
          color: white; font-family: var(--el-sans);
          font-size: 14px; font-weight: 700; letter-spacing: 0.02em;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 9px;
          position: relative; overflow: hidden;
          box-shadow: 0 6px 28px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.13);
          transition: transform 0.25s, box-shadow 0.25s, background 0.25s;
        }
        .el-btn:not(:disabled):hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 38px rgba(59,130,246,0.55), inset 0 1px 0 rgba(255,255,255,0.13);
          background: linear-gradient(135deg, #1D4ED8 0%, #3B82F6 50%, #60A5FA 100%);
        }
        .el-btn:not(:disabled):active { transform: translateY(0) scale(0.99); }
        .el-btn:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Shimmer sweep */
        .el-shimmer {
          position: absolute; inset: 0;
          background: linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.16) 50%, transparent 65%);
          transform: translateX(-100%); transition: transform 0.55s ease; pointer-events: none;
        }
        .el-btn:not(:disabled):hover .el-shimmer { transform: translateX(100%); }

        /* Spinner */
        .el-spin {
          width: 19px; height: 19px;
          border: 2.5px solid rgba(255,255,255,0.28);
          border-top-color: white; border-radius: 50%;
          animation: elspin 0.65s linear infinite;
        }
        @keyframes elspin { to { transform: rotate(360deg); } }

        /* ── Feature row ── */
        .el-feat {
          display: flex; align-items: center; gap: 12px;
          padding: 8px 0;
          border-bottom: 1px solid rgba(59,130,246,0.06);
          transition: transform 0.18s;
          cursor: default;
        }
        .el-feat:last-child { border-bottom: none; }
        .el-feat:hover { transform: translateX(5px); }

        /* ── Card ── */
        .el-card {
          background: linear-gradient(150deg, rgba(10,22,54,0.97) 0%, rgba(6,14,36,0.99) 100%);
          border: 1px solid rgba(59,130,246,0.18);
          border-radius: 22px;
          padding: 36px 32px;
          box-shadow:
            0 4px 60px rgba(0,0,0,0.72),
            0 0 0 1px rgba(59,130,246,0.05),
            0 0 80px rgba(37,99,235,0.07);
          position: relative; overflow: hidden;
        }

        /* ── Status bar ── */
        .el-statusbar {
          position: fixed; top: 0; left: 0; right: 0; height: 46px;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 28px;
          background: rgba(5,12,26,0.82); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
          border-bottom: 1px solid rgba(59,130,246,0.09);
          z-index: 50;
        }

        /* ══ LAYOUT ══ */
        .el-wrap {
          display: flex; align-items: center;
          width: 100%; max-width: 1120px;
          margin: 0 auto;
          gap: 64px;
          padding: 46px 60px 60px;
          min-height: 100vh; flex: 1;
          position: relative; z-index: 1;
        }
        .el-left  { flex: 1; min-width: 0; }
        .el-right { width: 450px; flex-shrink: 0; }

        /* ── Tablet ── */
        @media (max-width: 1100px) {
          .el-wrap  { gap: 44px; padding: 46px 36px 60px; }
          .el-right { width: 420px; }
        }

        /* ── Tablet small / mobile landscape ── */
        @media (max-width: 900px) {
          .el-left  { display: none !important; }
          .el-wrap  {
            justify-content: center;
            padding: 58px 24px 48px;
            align-items: flex-start;
          }
          .el-right { width: 100%; max-width: 500px; margin: 0 auto; }
        }

        /* ── Mobile ── */
        @media (max-width: 540px) {
          .el-statusbar { padding: 0 14px; }
          .el-statusbar .el-sr { display: none; }
          .el-wrap  { padding: 56px 14px 40px; align-items: flex-start; }
          .el-right { max-width: 100%; }
          .el-card  { padding: 22px 16px !important; border-radius: 18px !important; }
          .el-input { height: 46px; font-size: 13px; }
          .el-btn   { height: 46px; font-size: 13px; }
        }

        /* ── Very small ── */
        @media (max-width: 360px) {
          .el-wrap { padding: 54px 10px 32px; }
          .el-card { padding: 18px 13px !important; }
        }
      `}</style>

      {/* ── Background glow (follows mouse) ── */}
      <div style={{
        position: "fixed",
        width: 700, height: 700, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(37,99,235,0.13) 0%, transparent 65%)",
        top: `${mouse.y * 0.18 - 6}%`, left: `${mouse.x * 0.18 - 6}%`,
        filter: "blur(72px)", pointerEvents: "none", zIndex: 0,
        transition: "all 1.8s cubic-bezier(0.23,1,0.32,1)",
      }} />
      <div style={{
        position: "fixed",
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 65%)",
        bottom: "5%", right: "6%",
        filter: "blur(80px)", pointerEvents: "none", zIndex: 0,
      }} />

      {/* ══ STATUS BAR ══ */}
      <motion.div className="el-statusbar"
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <motion.div
            style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ADE80", boxShadow: "0 0 10px #4ADE80" }}
            animate={{ scale: [1, 1.55, 1], opacity: [1, 0.45, 1] }}
            transition={{ duration: 2.2, repeat: Infinity }}
          />
          <span style={{ fontFamily: "var(--el-mono)", fontSize: 10, color: "rgba(147,197,253,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Engine Online
          </span>
        </div>
        <div className="el-sr" style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Activity size={11} color="rgba(147,197,253,0.28)" />
            <span style={{ fontFamily: "var(--el-mono)", fontSize: 10, color: "rgba(147,197,253,0.22)" }}>v4.2.1</span>
          </div>
          <LiveClock />
        </div>
      </motion.div>

      {/* ══ MAIN WRAP ══ */}
      <div className="el-wrap">

        {/* ── LEFT PANEL ── */}
        <motion.div className="el-left"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Logo row */}
          <motion.div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 44 }}
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
            <motion.div
              whileHover={{ scale: 1.1, rotate: -6 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              style={{
                width: 52, height: 52, borderRadius: 15,
                background: "linear-gradient(135deg, #1E3A8A 0%, #2563EB 55%, #3B82F6 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 8px 32px rgba(37,99,235,0.52), 0 0 0 1px rgba(59,130,246,0.3)",
                position: "relative", overflow: "hidden",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "45%", background: "rgba(255,255,255,0.2)", borderRadius: "inherit" }} />
              <Zap size={24} color="white" />
            </motion.div>
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: 22, color: "var(--el-t1)", letterSpacing: "-0.02em", lineHeight: 1 }}>1401</div>
              <div style={{ fontFamily: "var(--el-mono)", fontSize: 10, color: "rgba(147,197,253,0.38)", letterSpacing: "0.1em", marginTop: 4, textTransform: "uppercase" }}>Payment Engine</div>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
            <h1 style={{
              fontFamily: "'Playfair Display', serif", fontSize: 46, fontWeight: 900,
              color: "var(--el-t1)", letterSpacing: "-0.035em", lineHeight: 1.15, margin: "0 0 14px",
            }}>
              Engine<br />
              <span style={{ background: "linear-gradient(135deg, #3B82F6, #60A5FA, #93C5FD)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Control Panel
              </span>
            </h1>
            <p style={{ fontSize: 14, color: "rgba(147,197,253,0.42)", lineHeight: 1.78, margin: "0 0 36px", maxWidth: 340, fontWeight: 400 }}>
              High-performance engine access for managing payment processing infrastructure with precision control.
            </p>
          </motion.div>

          {/* Features list */}
          <div style={{ maxWidth: 360 }}>
            {FEATURES.map((f, i) => (
              <motion.div key={f.label} className="el-feat"
                initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.38 + i * 0.07 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: `${f.color}18`, border: `1px solid ${f.color}30`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 0 12px ${f.color}20`,
                }}>
                  <f.icon size={15} color={f.color} />
                </div>
                <span style={{ fontSize: 13, color: "rgba(191,219,254,0.5)", fontWeight: 500 }}>{f.label}</span>
              </motion.div>
            ))}
          </div>

          {/* Trust tags */}
          <motion.div style={{ display: "flex", gap: 7, marginTop: 30, flexWrap: "wrap" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}>
            {["PCI DSS", "ISO 27001", "256-bit SSL", "SOC 2"].map((tag) => (
              <span key={tag} style={{
                padding: "3px 10px", borderRadius: 6,
                background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.16)",
                fontFamily: "var(--el-mono)", fontSize: 10,
                color: "rgba(147,197,253,0.36)", fontWeight: 600,
              }}>{tag}</span>
            ))}
          </motion.div>
        </motion.div>

        {/* ── RIGHT FORM ── */}
        <motion.div className="el-right"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          <div className="el-card">
            <CyberGrid />
            <ScanLine />
            <DataNodes />

            {/* Top shimmer line */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 1,
              background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.85) 35%, rgba(147,197,253,0.85) 65%, transparent)",
              zIndex: 3,
            }} />

            {/* Card header */}
            <div style={{ marginBottom: 24, position: "relative", zIndex: 3 }}>
              <motion.div
                whileHover={{ scale: 1.07, rotate: 5 }}
                transition={{ type: "spring", stiffness: 380, damping: 14 }}
                style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: "linear-gradient(135deg, #1E3A8A, #2563EB, #3B82F6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 18, cursor: "default",
                  boxShadow: "0 8px 32px rgba(37,99,235,0.5), 0 0 0 1px rgba(59,130,246,0.22)",
                  position: "relative", overflow: "hidden",
                }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "45%", background: "rgba(255,255,255,0.2)", borderRadius: "inherit" }} />
                <Zap size={23} color="white" />
              </motion.div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 25, fontWeight: 800, color: "var(--el-t1)", margin: 0, letterSpacing: "-0.025em" }}>
                Engine Login
              </h2>
              <p style={{ fontSize: 13, color: "rgba(147,197,253,0.36)", margin: "5px 0 0", fontWeight: 400 }}>
                Secure access · Authorized personnel only
              </p>
            </div>

            {/* ── FORM ── */}
            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 16, position: "relative", zIndex: 3 }}>

              {/* Loading stages */}
              <AnimatePresence>
                {loading && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    style={{ overflow: "hidden" }}>
                    <LoadingStages stage={stage} />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -8 }} animate={{ opacity: 1, height: "auto", y: 0 }} exit={{ opacity: 0, height: 0 }}
                    style={{ overflow: "hidden" }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 9,
                      padding: "10px 13px", borderRadius: 11,
                      background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.22)",
                      color: "#FCA5A5", fontSize: 13,
                    }}>
                      <AlertCircle size={14} style={{ flexShrink: 0, color: "#F87171" }} />
                      {error}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Success overlay */}
              <AnimatePresence>
                {success && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    style={{
                      position: "absolute", inset: 0, borderRadius: 22,
                      background: "rgba(6,14,36,0.96)", backdropFilter: "blur(14px)",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      gap: 16, zIndex: 20,
                    }}>
                    <motion.div
                      initial={{ scale: 0 }} animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 18 }}
                      style={{
                        width: 68, height: 68, borderRadius: "50%",
                        background: "rgba(59,130,246,0.15)", border: "2px solid #3B82F6",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 0 50px rgba(59,130,246,0.55)",
                      }}>
                      <Zap size={30} color="#3B82F6" />
                    </motion.div>
                    <div style={{ fontFamily: "var(--el-display)", fontSize: 19, fontWeight: 800, color: "var(--el-t1)" }}>Access Granted</div>
                    <div style={{ fontFamily: "var(--el-mono)", fontSize: 11, color: "rgba(147,197,253,0.45)", letterSpacing: "0.1em" }}>Redirecting to Engine Dashboard...</div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "rgba(147,197,253,0.55)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  {t("emailAddress") || "Email Address"}
                </label>
                <div style={{ position: "relative" }}>
                  <Mail size={15} style={{
                    position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)",
                    pointerEvents: "none", zIndex: 2,
                    color: focused === "email" ? "#3B82F6" : "rgba(147,197,253,0.24)",
                    transition: "color 0.22s",
                  }} />
                  <input
                    type="email" className="el-input"
                    placeholder="engine@company.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    onFocus={() => setFocused("email")} onBlur={() => setFocused("")}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "rgba(147,197,253,0.55)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  {t("password") || "Password"}
                </label>
                <div style={{ position: "relative" }}>
                  <Lock size={15} style={{
                    position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)",
                    pointerEvents: "none", zIndex: 2,
                    color: focused === "pass" ? "#3B82F6" : "rgba(147,197,253,0.24)",
                    transition: "color 0.22s",
                  }} />
                  <input
                    type={showPw ? "text" : "password"} className="el-input"
                    placeholder="••••••••"
                    value={password} onChange={e => setPassword(e.target.value)}
                    onFocus={() => setFocused("pass")} onBlur={() => setFocused("")}
                    style={{ paddingRight: 48 }}
                    required
                  />
                  <button
                    type="button" onClick={() => setShowPw(!showPw)}
                    style={{
                      position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer",
                      color: "rgba(147,197,253,0.28)", padding: 4, borderRadius: 7,
                      display: "flex", transition: "color 0.2s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "#60A5FA"}
                    onMouseLeave={e => e.currentTarget.style.color = "rgba(147,197,253,0.28)"}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "rgba(147,197,253,0.38)", fontWeight: 500, userSelect: "none" }}>
                  <input type="checkbox" style={{ accentColor: "#3B82F6", width: 14, height: 14 }} />
                  {t("rememberMe") || "Remember me"}
                </label>
                <a href="#"
                  style={{ fontSize: 13, fontWeight: 600, color: "#60A5FA", textDecoration: "none", transition: "color 0.18s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#93C5FD"}
                  onMouseLeave={e => e.currentTarget.style.color = "#60A5FA"}>
                  {t("forgotPassword") || "Forgot password?"}
                </a>
              </div>

              {/* Submit */}
              <button type="submit" className="el-btn" disabled={loading || success} style={{ marginTop: 2 }}>
                <div className="el-shimmer" />
                <AnimatePresence mode="wait">
                  {success ? (
                    <motion.span key="done" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                     <Zap size={17} />  Access Granted
                    </motion.span>
                  ) : loading ? (
                    <motion.span key="load" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <div className="el-spin" /> Authenticating...
                    </motion.span>
                  ) : (
                    <motion.span key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      {t("signIn") || "Sign in"} to Engine Panel <ArrowRight size={15} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </form>

            {/* Divider */}
            <div style={{ height: 1, background: "rgba(59,130,246,0.09)", margin: "22px 0 16px", position: "relative", zIndex: 3 }} />

            {/* Footer */}
            <div style={{ textAlign: "center", position: "relative", zIndex: 3 }}>
              <p style={{ fontFamily: "var(--el-mono)", fontSize: 10, color: "rgba(147,197,253,0.2)", margin: "0 0 6px", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                Access restricted to authorized personnel
              </p>
              <p style={{ fontSize: 13, color: "rgba(147,197,253,0.28)", margin: 0 }}>
                {t("needHelpContact") || "Need help? Contact"}{" "}
                <a href="mailto:support@1401.com"
                  style={{ color: "#60A5FA", textDecoration: "none", fontWeight: 600, transition: "color 0.18s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#93C5FD"}
                  onMouseLeave={e => e.currentTarget.style.color = "#60A5FA"}>
                  support@1401.com
                </a>
              </p>
            </div>
          </div>

          {/* Page footer */}
          <p style={{ textAlign: "center", fontFamily: "var(--el-mono)", fontSize: 10.5, color: "rgba(59,100,160,0.28)", marginTop: 18, letterSpacing: "0.04em" }}>
            © {new Date().getFullYear()} 1401 · {t("allRightsReserved") || "All rights reserved"}
          </p>
        </motion.div>
      </div>
    </div>
  );
}